import type React from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStatistics } from '@/hooks/useStatistics';
import { useStatisticsFromUrl } from '@/hooks/useStatisticsFromUrl';
import { useSplash } from '@/hooks/useSplash';
import { NO_REPORT } from '@/lib/api';
import { reportTokenFromHash } from '@/lib/utils';
import { ReportPageClient } from './ReportPageClient';
import { BlockReportCarousel } from './report/BlockReportCarousel';
import { SplashScreen } from './SplashScreen';
import { AlertCircle, BarChart3 } from 'lucide-react';

const LoadFailed: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3">
      <AlertCircle className="h-8 w-8 text-slate-400" />
      <p className="text-slate-500">{t('somethingWentWrong')}</p>
    </div>
  );
};

/** The match has no report (recorded only, or its analysis not ready yet): not an error. */
const NoReport: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-8 text-center">
      <BarChart3 className="h-8 w-8 text-slate-400" />
      <p className="text-lg font-semibold text-slate-700">{t('noReport')}</p>
      <p className="text-sm text-slate-500">{t('noReportHint')}</p>
    </div>
  );
};

/**
 * The sample advanced report players can try before buying (the Player
 * Dashboard's "Try it"): a real match the API serves as is, shown the way the
 * match report shows one — without the identification step, the viewer being
 * player 1.
 */
export const PreviewPage: React.FC = () => {
  const { data, loading, error } = useStatisticsFromUrl(
    `${import.meta.env.VITE_API_BASE_URL}/players/report-preview`,
  );
  const { phase, shouldRender } = useSplash({ dataReady: !loading });

  if (!loading && (error || !data)) return <LoadFailed />;

  return (
    <>
      {shouldRender && <SplashScreen phase={phase} loading={loading} />}

      {!loading && data && (
        <BlockReportCarousel
          segments={data.segments_statistics}
          identifications={{ 0: 'player1' }}
          predictionMode="advanced"
          splashDone={phase === 'hidden'}
        />
      )}
    </>
  );
};

const ReportPage: React.FC = () => {
  const { sessionId = 'local', playerId = 'local' } = useParams<{
    sessionId: string;
    playerId: string;
  }>();

  // Lets a signed-in owner (opened from the Player Dashboard) save their
  // identification under their account — read once, it doesn't change.
  const [reportToken] = useState(() =>
    reportTokenFromHash(window.location.hash),
  );
  // Asked as this viewer: an owner who corrected who is who gets their own report.
  const { data, loading, error, refetch } = useStatistics(sessionId, {
    playerId,
    reportToken,
  });
  const { phase, shouldRender } = useSplash({ dataReady: !loading });

  // Error or no data (after loading completes)
  if (!loading && error?.message === NO_REPORT) return <NoReport />;
  if (!loading && (error || !data)) return <LoadFailed />;

  return (
    <>
      {shouldRender && <SplashScreen phase={phase} loading={loading} />}

      {!loading && data && (
        <ReportPageClient
          data={data}
          sessionId={sessionId}
          externalPlayerId={playerId}
          reportToken={reportToken}
          onReload={refetch}
          // The report renders underneath the splash for its whole animation,
          // so "mounted" is not "seen". The guided tour waits for this.
          splashDone={phase === 'hidden'}
        />
      )}
    </>
  );
};

export default ReportPage;
